import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const PRO_TOKEN_KEY = "scorpionpdf_pro_token";
const PRO_PLAN_KEY = "scorpionpdf_pro_plan";
// chaves antigas (marca anterior): migradas transparentemente na primeira leitura,
// para nao desconectar quem ja tinha o plano ativo no navegador
const LEGACY_KEYS = { kinetic_pro_token: PRO_TOKEN_KEY, kinetic_pro_plan: PRO_PLAN_KEY };

function migrateLegacy(key) {
    const legacyKey = Object.keys(LEGACY_KEYS).find((k) => LEGACY_KEYS[k] === key);
    const legacy = legacyKey ? localStorage.getItem(legacyKey) : null;
    if (legacy) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(legacyKey);
        return legacy;
    }
    return null;
}

export function getProToken() {
    return localStorage.getItem(PRO_TOKEN_KEY) || migrateLegacy(PRO_TOKEN_KEY);
}

export function setProToken(token) {
    if (token) localStorage.setItem(PRO_TOKEN_KEY, token);
    else localStorage.removeItem(PRO_TOKEN_KEY);
    localStorage.removeItem("kinetic_pro_token");
}

export function getProPlan() {
    const p = localStorage.getItem(PRO_PLAN_KEY) || migrateLegacy(PRO_PLAN_KEY);
    return p === "business" || p === "pro" ? p : null;
}

export function setProPlan(plan) {
    if (plan === "business" || plan === "pro") localStorage.setItem(PRO_PLAN_KEY, plan);
    else localStorage.removeItem(PRO_PLAN_KEY);
    localStorage.removeItem("kinetic_pro_plan");
}

export const PLAN_LIMITS = {
    free: { maxFileMB: 25, maxFiles: 10, maxPages: 200 },
    pro: { maxFileMB: 100, maxFiles: 50, maxPages: 1000 },
    business: { maxFileMB: 250, maxFiles: 100, maxPages: 2500 },
};

export function currentLimits() {
    if (!getProToken()) return PLAN_LIMITS.free;
    return PLAN_LIMITS[getProPlan() || "pro"] || PLAN_LIMITS.pro;
}

export async function createJob(toolSlug, files, options, onUploadProgress, idempotencyKey, sequence) {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("options", JSON.stringify(options || {}));
    if (sequence) {
        form.append("ids", JSON.stringify(sequence.ids));
        form.append("order", JSON.stringify(sequence.order));
        form.append("names", JSON.stringify(sequence.names));
    }
    const headers = { "Content-Type": "multipart/form-data" };
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    const proToken = getProToken();
    if (proToken) headers["X-Pro-Token"] = proToken;
    const res = await axios.post(`${API}/tools/${toolSlug}/jobs`, form, {
        headers,
        onUploadProgress,
    });
    return res.data;
}

export async function getJob(jobId, token) {
    const res = await axios.get(`${API}/jobs/${jobId}`, { params: { token } });
    return res.data;
}

export function downloadUrl(jobId, token) {
    return `${API}/jobs/${jobId}/download?token=${encodeURIComponent(token)}`;
}

export async function sendContact(payload) {
    const res = await axios.post(`${API}/contact`, payload);
    return res.data;
}

export async function getPublicStatus() {
    const res = await axios.get(`${API}/status-publico`);
    return res.data;
}

export async function getFlowOperations() {
    const headers = {};
    const proToken = getProToken();
    if (proToken) headers["X-Pro-Token"] = proToken;
    const res = await axios.get(`${API}/flows/operations`, { headers });
    return res.data;
}

export async function getUsage() {
    const headers = {};
    const proToken = getProToken();
    if (proToken) headers["X-Pro-Token"] = proToken;
    const res = await axios.get(`${API}/uso`, { headers });
    return res.data;
}

export async function createFlowJob(files, operations, idempotencyKey, onUploadProgress, sequence) {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("flow", JSON.stringify({ operations }));
    if (sequence) {
        form.append("ids", JSON.stringify(sequence.ids));
        form.append("order", JSON.stringify(sequence.order));
        form.append("names", JSON.stringify(sequence.names));
    }
    const headers = { "Content-Type": "multipart/form-data", "Idempotency-Key": idempotencyKey };
    const proToken = getProToken();
    if (proToken) headers["X-Pro-Token"] = proToken;
    const res = await axios.post(`${API}/flows/jobs`, form, { headers, onUploadProgress });
    return res.data;
}

export async function createCheckout(plan = "pro", period = "monthly") {
    const res = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
        plan,
        period,
    });
    return res.data;
}

export async function getPlans() {
    const res = await axios.get(`${API}/plans`);
    return res.data;
}

export async function getPaymentStatus(sessionId) {
    const res = await axios.get(`${API}/payments/status/${sessionId}`);
    return res.data;
}

export async function verifyPro(token) {
    const res = await axios.get(`${API}/pro/verify`, { headers: { "X-Pro-Token": token } });
    return res.data;
}

export async function openProPortal(token) {
    const res = await axios.post(`${API}/pro/portal`, {
        token,
        origin_url: window.location.origin,
    });
    return res.data;
}

export function errorMessage(err) {
    return (
        err?.response?.data?.detail ||
        "Não foi possível concluir agora. Verifique sua conexão e tente novamente."
    );
}

/** Leitor de SSE (Server-Sent Events) para as ferramentas de IA. */
export async function streamForm(path, formData, handlers) {
    const headers = {};
    const proToken = getProToken();
    if (proToken) headers["X-Pro-Token"] = proToken;
    const res = await fetch(`${API}${path}`, { method: "POST", body: formData, headers });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Não foi possível concluir agora. Tente novamente.");
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data: ")) continue;
            let payload;
            try { payload = JSON.parse(line.slice(6)); } catch { continue; }
            if (payload.meta) handlers.onMeta?.(payload.meta);
            if (payload.delta) handlers.onDelta?.(payload.delta);
            if (payload.error) handlers.onError?.(payload.error);
            if (payload.done) handlers.onDone?.();
        }
    }
}

export async function aiFollowUp(sessionId, token, question, handlers) {
    const res = await fetch(`${API}/ai/perguntar/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, question }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "A sessão não está mais disponível.");
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data: ")) continue;
            let payload;
            try { payload = JSON.parse(line.slice(6)); } catch { continue; }
            if (payload.meta) handlers.onMeta?.(payload.meta);
            if (payload.delta) handlers.onDelta?.(payload.delta);
            if (payload.error) handlers.onError?.(payload.error);
            if (payload.done) handlers.onDone?.();
        }
    }
}

// ---- Historico Pro (Object Store) ----

export async function saveToHistory(jobId, jobToken) {
    const res = await axios.post(
        `${API}/historico/${jobId}`,
        { token: jobToken },
        { headers: { "X-Pro-Token": getProToken() || "" } }
    );
    return res.data;
}

export async function listHistory() {
    const res = await axios.get(`${API}/historico`, {
        headers: { "X-Pro-Token": getProToken() || "" },
    });
    return res.data;
}

export async function downloadHistoryItem(item) {
    const res = await axios.get(`${API}/historico/${item.id}/download`, {
        headers: { "X-Pro-Token": getProToken() || "" },
        responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.original_name;
    a.click();
    URL.revokeObjectURL(url);
}

export async function deleteHistoryItem(id) {
    const res = await axios.delete(`${API}/historico/${id}`, {
        headers: { "X-Pro-Token": getProToken() || "" },
    });
    return res.data;
}

// ---- Plano Business (equipe) ----

export async function listMembers() {
    const res = await axios.get(`${API}/business/members`, {
        headers: { "X-Pro-Token": getProToken() || "" },
    });
    return res.data;
}

export async function createMember(label) {
    const res = await axios.post(
        `${API}/business/members`,
        { label },
        { headers: { "X-Pro-Token": getProToken() || "" } }
    );
    return res.data;
}

export async function deleteMember(id) {
    const res = await axios.delete(`${API}/business/members/${id}`, {
        headers: { "X-Pro-Token": getProToken() || "" },
    });
    return res.data;
}
